import { NgClass, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AdvancedSettingService,
  AuthSevices,
  ClientSubscriptionService,
  CommonService,
  ToastMessageService,
} from '@fitness-force/api';
import {
  RecurringOccuranceType,
  TimelinePricingAdjustmentScope,
} from '@fitness-force/enum';
import type { AppErrors, InvoiceOccurance } from '@fitness-force/models';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TextareaModule } from 'primeng/textarea';
import { take } from 'rxjs';

@Component({
  selector: 'app-edit-payment-timeline-popup',
  templateUrl: './edit-payment-timeline-popup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    SelectButtonModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    CurrencyformatPipe,
    DateTimeFormatPipe,
    ErrorMsgComponent,
    TranslateModule,
    NgClass,
    MessageModule,
    DecimalPipe,
  ],
})
export class EditPaymentTimelinePopupComponent {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthSevices);
  private readonly advanceSettingService = inject(AdvancedSettingService);
  private readonly clientSubscriptionService = inject(ClientSubscriptionService);
  private readonly commonService = inject(CommonService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);

  readonly visible = input(false);
  readonly occurrence = input<InvoiceOccurance | null>(null);
  readonly closePopup = output<void>();
  readonly saved = output<void>();

  isSubmitting = signal(false);
  recurringStaffDiscount = signal(0);
  isTaxInclusive = signal(true);
  invalidRecurringDiscount = signal(false);
  discountBaseAmountMinor = signal(0);
  appliedDiscountAmountMinor = signal(0);
  appliedDiscountPercent = signal(0);

  maxDiscountAmountMinor = computed(() => {
    const baseMinor = this.discountBaseAmountMinor();
    return Math.round((baseMinor * this.recurringStaffDiscount()) / 100);
  });

  /** Lowest allowed new amount after applying the staff's maximum discount %. */
  minDiscountedAmountMinor = computed(() => {
    const baseMinor = this.discountBaseAmountMinor();
    return baseMinor - this.maxDiscountAmountMinor();
  });

  taxCalculationLabelKey = computed(() =>
    this.isTaxInclusive()
      ? 'SUBSCRIPTIONS.PAYMENT_TIMELINE.EDIT.TAX_INCLUSIVE'
      : 'SUBSCRIPTIONS.PAYMENT_TIMELINE.EDIT.TAX_EXCLUSIVE',
  );

  private readonly adjustmentScopeLabels = signal<{
    thisOnly: string;
    thisAndFuture: string;
  } | null>(null);

  readonly isEnhancementOccurrence = computed(() => {
    const selectedOccurrence = this.occurrence();
    if (!selectedOccurrence) {
      return false;
    }

    return (
      this.resolveOccurrenceType(selectedOccurrence.occurance_type) ===
      RecurringOccuranceType.Enhancement
    );
  });

  readonly adjustmentScopeOptions = computed(() => {
    const labels = this.adjustmentScopeLabels();
    if (!labels) {
      return [];
    }

    const thisOnlyOption = {
      label: labels.thisOnly,
      value: TimelinePricingAdjustmentScope.ThisOnly,
    };

    if (this.isEnhancementOccurrence()) {
      return [thisOnlyOption];
    }

    return [
      thisOnlyOption,
      {
        label: labels.thisAndFuture,
        value: TimelinePricingAdjustmentScope.ThisAndFuture,
      },
    ];
  });

  editForm = this.fb.group({
    adjustment_scope: [
      TimelinePricingAdjustmentScope.ThisOnly,
      Validators.required,
    ],
    new_amount: [null as number | null, [Validators.required, Validators.min(0)]],
    adjustment_reason_code: [''],
    adjustment_notes: [''],
  });

  constructor() {
    const currentUserDetails = this.authService.decodedToken;
    this.recurringStaffDiscount.set(currentUserDetails?.recurring_staff_discount ?? 0);

    this.translate
      .get([
        'SUBSCRIPTIONS.PAYMENT_TIMELINE.EDIT.THIS_ONLY',
        'SUBSCRIPTIONS.PAYMENT_TIMELINE.EDIT.THIS_AND_FUTURE',
      ])
      .subscribe((translations) => {
        this.adjustmentScopeLabels.set({
          thisOnly: translations['SUBSCRIPTIONS.PAYMENT_TIMELINE.EDIT.THIS_ONLY'],
          thisAndFuture:
            translations['SUBSCRIPTIONS.PAYMENT_TIMELINE.EDIT.THIS_AND_FUTURE'],
        });
      });

    effect(() => {
      const selectedOccurrence = this.occurrence();
      if (!selectedOccurrence || !this.visible()) {
        return;
      }

      this.loadDiscountContext(selectedOccurrence);
    });

    this.AdjustmentScopeControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (
          this.isEnhancementOccurrence() &&
          value !== TimelinePricingAdjustmentScope.ThisOnly
        ) {
          this.AdjustmentScopeControl?.setValue(TimelinePricingAdjustmentScope.ThisOnly, {
            emitEvent: false,
          });
        }
      });
  }

  get AdjustmentScopeControl() {
    return this.editForm.get('adjustment_scope');
  }

  get NewAmountControl() {
    return this.editForm.get('new_amount');
  }

  get ReasonCodeControl() {
    return this.editForm.get('adjustment_reason_code');
  }

  get NotesControl() {
    return this.editForm.get('adjustment_notes');
  }

  onClose(): void {
    this.closePopup.emit();
  }

  onNewAmountBlur(): void {
    this.NewAmountControl?.markAsTouched();
    this.calculateRecurringDiscount();
  }

  onNewAmountInput(): void {
    if (this.NewAmountControl?.touched) {
      this.calculateRecurringDiscount();
    }
  }

  onSubmit(): void {
    this.editForm.markAllAsTouched();
    this.calculateRecurringDiscount();

    const selectedOccurrence = this.occurrence();
    if (
      !selectedOccurrence ||
      this.editForm.invalid ||
      this.invalidRecurringDiscount()
    ) {
      return;
    }

    const formValue = this.editForm.getRawValue();
    const newAmount = formValue.new_amount;

    if (newAmount === null || newAmount === undefined) {
      return;
    }

    this.isSubmitting.set(true);

    this.clientSubscriptionService
      .EditTimelinePricing(
        selectedOccurrence.occurance_tenant_id,
        selectedOccurrence.occurance_client_id,
        selectedOccurrence.occurance_subscription_id,
        {
          occurrence_id: selectedOccurrence.occurance,
          adjustment_scope: formValue.adjustment_scope as TimelinePricingAdjustmentScope,
          new_total_amount_in_minor_currency:this.commonService.ConvertMinorCurrencyFromAmount(newAmount),
          adjustment_notes: formValue.adjustment_notes?.trim() || '',
          adjustment_reason_code: formValue.adjustment_reason_code?.trim() || '',
          occurrence_type: selectedOccurrence.occurance_type,
        },
      )
      .subscribe({
        next: () => {
          this.translate
            .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.EDIT.SUCCESS_MSG')
            .subscribe((message: string) => {
              this.toastMessageService.showSuccess(message);
            });
          this.isSubmitting.set(false);
          this.saved.emit();
          this.onClose();
        },
        error: (error: AppErrors) => {
          this.isSubmitting.set(false);
          this.toastMessageService.showError(error);
        },
      });
  }

  private loadDiscountContext(occurrence: InvoiceOccurance): void {
    this.invalidRecurringDiscount.set(false);
    this.appliedDiscountAmountMinor.set(0);
    this.appliedDiscountPercent.set(0);

    this.advanceSettingService
      .getAdvanceSettings(occurrence.occurance_tenant_id)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // API returns { items: TenantSettingByKey[] } — service typing is incomplete
          const settings = res as unknown as { items?: { key: string; value: string }[] };
          const taxCalculation = settings.items?.find(
            (item) => item.key.toLowerCase() === 'tax_calculation',
          );
          this.isTaxInclusive.set(taxCalculation?.value?.toLowerCase() === 'inclusive');
          this.setDiscountBaseAmount(occurrence);
          this.resetFormForOccurrence();
        },
        error: () => {
          this.isTaxInclusive.set(true);
          this.setDiscountBaseAmount(occurrence);
          this.resetFormForOccurrence();
        },
      });
  }

  private setDiscountBaseAmount(occurrence: InvoiceOccurance): void {
    const baseAmountMinor = this.isTaxInclusive()
      ? occurrence.invoice_amount_in_minor_currency
      : occurrence.occurance_recurring_service?.reduce(
          (sum, service) => sum + (service.svc_base_cost_in_minor_curr ?? 0),
          0,
        ) ?? occurrence.invoice_amount_in_minor_currency;

    this.discountBaseAmountMinor.set(baseAmountMinor);
    this.updateAmountValidators();
  }

  private resetFormForOccurrence(): void {
    this.invalidRecurringDiscount.set(false);
    this.appliedDiscountAmountMinor.set(0);
    this.appliedDiscountPercent.set(0);
    this.editForm.reset({
      adjustment_scope: TimelinePricingAdjustmentScope.ThisOnly,
      new_amount: null,
      adjustment_reason_code: '',
      adjustment_notes: '',
    });
  }

  private resolveOccurrenceType(
    occuranceType: RecurringOccuranceType | string | number,
  ): RecurringOccuranceType | undefined {
    if (typeof occuranceType === 'number') {
      return occuranceType as RecurringOccuranceType;
    }

    return RecurringOccuranceType[
      occuranceType.toString() as keyof typeof RecurringOccuranceType
    ];
  }

  private updateAmountValidators(): void {
    const newAmountControl = this.NewAmountControl;
    if (!newAmountControl) {
      return;
    }

    newAmountControl.setValidators([Validators.required]);
    newAmountControl.updateValueAndValidity({ emitEvent: false });
  }

  private calculateRecurringDiscount(): void {
    const baseAmountMinor = this.discountBaseAmountMinor();
    const newAmount = this.NewAmountControl?.value;

    if (newAmount === null || newAmount === undefined || baseAmountMinor <= 0) {
      this.invalidRecurringDiscount.set(false);
      this.appliedDiscountAmountMinor.set(0);
      this.appliedDiscountPercent.set(0);
      return;
    }

    const newAmountMinor = this.commonService.ConvertMinorCurrencyFromAmount(newAmount);

    if (newAmountMinor >= baseAmountMinor) {
      this.invalidRecurringDiscount.set(false);
      this.appliedDiscountAmountMinor.set(0);
      this.appliedDiscountPercent.set(0);
      return;
    }

    const minDiscountedAmountMinor = this.minDiscountedAmountMinor();
    const discountAmountMinor = baseAmountMinor - newAmountMinor;
    const isWithinStaffDiscountLimit = newAmountMinor >= minDiscountedAmountMinor;

    if (isWithinStaffDiscountLimit) {
      this.appliedDiscountPercent.set(
        baseAmountMinor > 0 ? (discountAmountMinor / baseAmountMinor) * 100 : 0,
      );
      this.appliedDiscountAmountMinor.set(Math.max(0, discountAmountMinor));
      this.invalidRecurringDiscount.set(false);
      return;
    }

    this.appliedDiscountPercent.set(0);
    this.appliedDiscountAmountMinor.set(0);
    this.invalidRecurringDiscount.set(true);
    this.NewAmountControl?.markAsTouched();
  }
}
